// API Configuration
// In production, API_BASE_URL points to the Supabase Edge Function: /functions/v1/api
// All paths below are relative to the "api" Edge Function's internal router.
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

// Edge Functions base URL (for non-api functions like payments, upload, validate, webhook)
export const EDGE_FUNCTIONS_URL = process.env.REACT_APP_EDGE_FUNCTIONS_URL || API_BASE_URL;

// API Endpoints — paths relative to API_BASE_URL (routed by api/index.ts)
export const API_ENDPOINTS = {
  // Instructions (routed by api function)
  INSTRUCTIONS: {
    GET_ALL: '/instructions',
    GET_BY_FIELD: (fieldName: string) => `/instructions/${fieldName}`,
    CREATE: (category: string) => `/instructions/${category}`,
    UPDATE: (fieldName: string) => `/instructions/${fieldName}`,
    DELETE: (fieldName: string) => `/instructions/${fieldName}`,
  },
  // Category-based endpoints
  CATEGORY: {
    INSTRUCTIONS: (category: string) => `/instructions/${category}`,
    INSTRUCTIONS_BY_SUBCATEGORY: (subcategory: string) => `/instructions/subcategory/${subcategory}`,
    VISIBILITY: (subcategory: string) => `/instructions/subcategory/${subcategory}/visibility`,
    REMARK: (subcategory: string) => `/instructions/subcategory/${subcategory}/remark`,
  },
  // Debug
  DEBUG: {
    TABLES: '/debug/tables',
  },
  // Health
  HEALTH: '/health',
  // Authentication
  AUTH: {
    REGISTER: '/auth/register',
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    PROFILE: '/auth/profile',
    CHANGE_PASSWORD: '/auth/change-password',
    PERMISSIONS: '/auth/permissions',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    USERS: '/auth/users',
  },
  // Schemas (public)
  SCHEMAS: '/schemas',
  SCHEMAS_LOOKUP: '/schemas/lookup',
  // Dynamic Data
  DYNAMIC_DATA: {
    GET_DATA: (category: string) => `/dynamic-data/${category}`,
    GET_COLUMNS: (category: string) => `/dynamic-data/${category}/columns`,
    INSERT_DATA: (category: string) => `/dynamic-data/${category}`,
    UPDATE_DATA: (category: string, id: string) => `/dynamic-data/${category}/${id}`,
    DELETE_DATA: (category: string, id: string) => `/dynamic-data/${category}/${id}`
  },
  // Categories
  CATEGORIES: {
    ALL: '/categories',
    MAIN: '/categories/main',
    SUBCATEGORIES: (mainCategoryId: number | string) => `/categories/${mainCategoryId}/subcategories`,
    SUBCATEGORIES_BY_NAME: (name: string) => `/categories/name/${name}/subcategories`,
    CREATE_MAIN: '/categories/main',
    CREATE_SUB: '/categories/subcategory',
    DELETE_MAIN: (id: number | string) => `/categories/main/${id}`,
    DELETE_SUB: (id: number | string) => `/categories/subcategory/${id}`,
  },
  // Organizations
  ORGANIZATIONS: {
    CREATE: '/organizations',
    MY_ORG: '/organizations/my-org',
    ASSIGN_USER: '/organizations/assign-user',
    ALL: '/organizations/all',
    MEMBERS: (orgId: string) => `/organizations/${orgId}/members`,
    SETTINGS: (orgId: string) => `/organizations/${orgId}/settings`,
    DELETE: (orgId: string) => `/organizations/${orgId}`,
  },
  // Users (schema-scoped)
  USERS: {
    LIST: '/users',
    CREATE: '/users',
    GET: (userId: string) => `/users/${userId}`,
    UPDATE: (userId: string) => `/users/${userId}`,
    DELETE: (userId: string) => `/users/${userId}`,
    DEACTIVATE: (userId: string) => `/users/${userId}/deactivate`,
    ACTIVATE: (userId: string) => `/users/${userId}/activate`,
    PERMISSIONS: (userId: string) => `/users/${userId}/permissions`,
  },
  // Roles & Departments
  ROLES: '/roles',
  DEPARTMENTS: '/departments',
  MODULES: '/modules',
} as const;

// Separate Edge Function endpoints (NOT routed through /api)
export const EDGE_ENDPOINTS = {
  // Payments (payments Edge Function)
  PAYMENTS: {
    PLANS: '/payments/plans',
    CREATE_INTENT: '/payments/create-payment-intent',
    CREATE_SUBSCRIPTION: '/payments/create-subscription',
    CONFIRM: '/payments/confirm-payment',
    MY_PLAN: '/payments/my-plan',
    USAGE_HISTORY: '/payments/usage-history',
    ADMIN_USAGE: '/payments/admin/all-orgs-usage',
    HISTORY: '/payments/history',
  },
  // Validation (validate Edge Function — async queue)
  VALIDATE: {
    SUBMIT: '/validate/submit',
    STATUS: (jobId: string) => `/validate/status/${jobId}`,
  },
  // Upload (upload Edge Function)
  UPLOAD: {
    SCHEMA: '/upload/upload-schema',
    FIELD_INSTRUCTIONS: '/upload/upload-field-instructions',
  },
  // Webhook (webhook Edge Function)
  WEBHOOK: '/webhook',
} as const;

// Request timeout
export const REQUEST_TIMEOUT = 120000; // 2 minutes for large datasets

// File upload limits
export const FILE_UPLOAD = {
  MAX_SIZE: 50 * 1024 * 1024, // 50MB
  ALLOWED_TYPES: [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-excel', // .xls
    'text/csv', // .csv
  ],
} as const;
